// Batch-Verortung der Kundenadressen (ERP/CRM-Plan, Abschnitt 3.3).
//
// Warum ein Job und kein Geocoding beim Rendern: Nominatim erlaubt ~1 Anfrage
// pro Sekunde. Eine Karte mit 400 Kunden waere damit sieben Minuten lang am
// Laden — und wuerde bei jedem Oeffnen dieselben Adressen erneut erfragen.
// Stattdessen laeuft dieser Job einmal ueber den Bestand, legt jedes Ergebnis
// in `geo_cache` ab und schreibt die Koordinaten an den Kunden.
//
// Zwei Regeln, die der Job nie bricht:
//   1. `geoManual = true` wird NIE ueberschrieben (weder ausgewaehlt noch
//      geschrieben — der Filter greift hier, die `.eq('geo_manual', false)`
//      in crmService.updateCustomerGeo ist der zweite Riegel).
//   2. Ein Fehlschlag wird gespeichert (`quality = 'failed'`, `attempts`),
//      damit der naechste Lauf nicht erneut dagegen laeuft.
//
// Die Kernlogik (Kandidatenauswahl, Klassifikation, Backoff) ist pur und ohne
// Netz pruefbar; unrein ist allein runGeocodeBackfill().

import { fetchCustomers, updateCustomerGeo } from './crmService';
import {
    GeoCacheEntry,
    GeoQuality,
    addressKey,
    hasCoordinates,
    lookupGeoCacheMany,
    upsertGeoCache,
} from './geoCache';

// ─── Kandidatenauswahl (pur) ────────────────────────────────────────────────

/** Kundenfelder, die der Job braucht — `Customer` erfuellt das strukturell. */
export interface GeocodeCustomerLike {
    id: string;
    name: string;
    street?: string;
    zip?: string;
    city?: string;
    lat?: number;
    lng?: number;
    geoManual?: boolean;
    archivedAt?: string;
    mergedIntoId?: string;
}

/** Ein zu verortender Kunde, fertig aufbereitet. */
export interface GeocodeCandidate {
    customerId: string;
    name: string;
    /** Anzeige-/Abfrageform der Adresse (Originalschreibweise). */
    query: string;
    /** Vergleichsform — Schluessel des Caches. */
    addressNorm: string;
    addressHash: string;
    /** Ohne Strasse ist bestenfalls Ortsgenauigkeit erreichbar. */
    hasStreet: boolean;
}

export interface GeocodePlanOptions {
    /** Hoechstzahl Kandidaten je Lauf (Nominatim-Budget). */
    limit?: number;
    /** true = auch bereits verortete Kunden erneut verorten (ausser manuelle). */
    refresh?: boolean;
    /** true = archivierte/zusammengefuehrte Kunden mitnehmen (Standard: nein). */
    includeArchived?: boolean;
}

export interface GeocodeBackfillPlan {
    candidates: GeocodeCandidate[];
    /** Gesamtzahl geprüfter Kunden. */
    total: number;
    /** Von Hand verortet — unantastbar. */
    skippedManual: number;
    /** Keine verwertbare Adresse hinterlegt. */
    skippedWithoutAddress: number;
    /** Bereits verortet (ohne `refresh`). */
    skippedGeocoded: number;
    /** Archiviert oder zusammengefuehrt. */
    skippedArchived: number;
    /** Wegen `limit` diesmal nicht mitgenommen. */
    deferred: number;
}

/**
 * Wer wird verortet? Rein — die Auswahl ist die eigentliche Fachlogik des Jobs
 * und muss ohne Datenbank pruefbar bleiben.
 *
 * Die Adresse wird aus street/zip/city gebildet, NICHT aus dem Altfeld
 * `address`: dessen Freitext ist nicht zuverlaessig zerlegbar (der Parser in
 * CustomerDetail ist eine Heuristik). Wer nur die Alt-Adresse hat, taucht als
 * "ohne Adresse" auf und wird im Kundendetail per "Übernehmen" nachgepflegt.
 */
export const planGeocodeBackfill = (
    customers: GeocodeCustomerLike[] | null | undefined,
    options: GeocodePlanOptions = {},
): GeocodeBackfillPlan => {
    const limit = options.limit;
    const plan: GeocodeBackfillPlan = {
        candidates: [],
        total: 0,
        skippedManual: 0,
        skippedWithoutAddress: 0,
        skippedGeocoded: 0,
        skippedArchived: 0,
        deferred: 0,
    };

    for (const c of customers || []) {
        if (!c?.id) continue;
        plan.total++;

        if (!options.includeArchived && (c.archivedAt || c.mergedIntoId)) {
            plan.skippedArchived++;
            continue;
        }
        // Zuerst pruefen: eine von Hand gesetzte Koordinate bleibt auch dann
        // unangetastet, wenn jemand `refresh` anfordert.
        if (c.geoManual) {
            plan.skippedManual++;
            continue;
        }
        const verortet = Number.isFinite(c.lat as number) && Number.isFinite(c.lng as number);
        if (verortet && !options.refresh) {
            plan.skippedGeocoded++;
            continue;
        }

        const { norm, hash } = addressKey(c.street, c.zip, c.city);
        if (!norm) {
            plan.skippedWithoutAddress++;
            continue;
        }
        if (limit !== undefined && plan.candidates.length >= limit) {
            plan.deferred++;
            continue;
        }

        const street = (c.street || '').trim();
        const query = [street, [(c.zip || '').trim(), (c.city || '').trim()].filter(Boolean).join(' ')]
            .filter(Boolean)
            .join(', ');
        plan.candidates.push({
            customerId: c.id,
            name: c.name || '',
            query,
            addressNorm: norm,
            addressHash: hash,
            hasStreet: !!street,
        });
    }

    return plan;
};

// ─── Ergebnis-Klassifikation (pur) ──────────────────────────────────────────

// Nominatim beschreibt einen Treffer ueber `class`/`type` und (neuer)
// `addresstype`. Die Zuordnung unten ist bewusst konservativ: im Zweifel lieber
// 'interpolated' als 'exact' — die Karte darf keine Genauigkeit vorspiegeln,
// die der Treffer nicht hat.
const EXACT_TYPES = new Set(['house', 'house_number', 'building', 'address', 'apartments']);
const STREET_TYPES = new Set(['road', 'street', 'residential', 'living_street', 'pedestrian', 'unclassified']);
const PLACE_TYPES = new Set([
    'postcode', 'city', 'town', 'village', 'hamlet', 'municipality',
    'suburb', 'quarter', 'borough', 'city_district', 'county', 'state',
]);

/** Rohtreffer von Nominatim, so weit der Job ihn auswertet. */
export interface NominatimHit {
    lat?: string | number;
    lon?: string | number;
    class?: string;
    type?: string;
    addresstype?: string;
    display_name?: string;
}

export interface GeocodeClassification {
    lat: number;
    lng: number;
    quality: GeoQuality;
    displayName?: string;
}

/**
 * Rohtreffer -> Koordinate + Guete. null, wenn nichts Verwertbares dabei ist.
 *
 * `hasStreet = false` deckelt das Ergebnis auf 'city': ohne Strasse in der
 * Abfrage KANN der Treffer nicht hausgenau sein, auch wenn Nominatim zufaellig
 * ein Gebaeude zurueckgibt.
 */
export const classifyGeocodeResult = (
    hit: NominatimHit | null | undefined,
    opts: { hasStreet?: boolean } = {},
): GeocodeClassification | null => {
    const lat = Number(hit?.lat);
    const lng = Number(hit?.lon);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;

    const kind = String(hit?.addresstype || hit?.type || '').toLowerCase();
    const klasse = String(hit?.class || '').toLowerCase();

    let quality: GeoQuality;
    if (EXACT_TYPES.has(kind) || klasse === 'building') quality = 'exact';
    else if (STREET_TYPES.has(kind) || klasse === 'highway') quality = 'interpolated';
    else if (PLACE_TYPES.has(kind)) quality = 'city';
    // Alles Uebrige sind Sonderfaelle (Firmen-POI, Gewerbegebiet, Gebaeudeteil).
    // Sie liegen am richtigen Ort, aber nicht nachweisbar an der Hausnummer.
    else quality = 'interpolated';

    if (!opts.hasStreet && quality !== 'city') quality = 'city';

    return { lat, lng, quality, displayName: hit?.display_name ? String(hit.display_name) : undefined };
};

/** Nach so vielen erfolglosen Versuchen ist eine Adresse Handarbeit. */
export const MAX_GEOCODE_ATTEMPTS = 3;

/**
 * Darf ein bekannter Fehlschlag erneut versucht werden? Ohne diesen Backoff
 * verbraucht jeder Lauf sein Anfragebudget an denselben unaufloesbaren
 * Adressen und kommt nie beim Rest an.
 */
export const shouldRetryCacheEntry = (
    entry: GeoCacheEntry | null | undefined,
    maxAttempts: number = MAX_GEOCODE_ATTEMPTS,
): boolean => {
    if (!entry) return true;
    if (entry.manual) return false;
    if (hasCoordinates(entry)) return false;
    return (entry.attempts ?? 0) < maxAttempts;
};

// ─── Lauf (unrein) ──────────────────────────────────────────────────────────

export interface GeocodeFailure {
    customerId: string;
    name: string;
    address: string;
    /** Deutscher Klartext fuer die Fehlerliste in der Oberflaeche. */
    reason: string;
}

export interface GeocodeProgress {
    done: number;
    total: number;
    /** Name des gerade bearbeiteten Kunden. */
    current: string;
}

export interface GeocodeBackfillOptions extends GeocodePlanOptions {
    /** Nur zaehlen und auflisten, nichts schreiben und nichts abfragen. */
    dryRun?: boolean;
    onProgress?: (p: GeocodeProgress) => void;
    /** Kundenliste vorgeben statt zu laden (Tests, Teilmengen). */
    customers?: GeocodeCustomerLike[];
    /** Geocoder-Ersatz; Standard ist Nominatim ueber /api/osm-proxy. */
    geocode?: (query: string) => Promise<NominatimHit | null>;
    /** Pause zwischen zwei Netzaufrufen; Nominatim-Policy: ~1 req/s. */
    throttleMs?: number;
    maxAttempts?: number;
}

export interface GeocodeBackfillResult extends GeocodeBackfillPlan {
    dryRun: boolean;
    /** Aus dem Cache beantwortet — ohne Netzanfrage. */
    fromCache: number;
    /** Neu bei Nominatim erfragt und aufgeloest. */
    geocoded: number;
    /** Tatsaechlich am Kunden geschrieben. */
    updated: number;
    failures: GeocodeFailure[];
}

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/**
 * Nominatim ueber den eigenen Proxy — derselbe Weg wie osmMaps.geocode(), hier
 * aber mit dem ROHEN Treffer: die Guete-Einstufung braucht `class`/`type`, die
 * osmMaps auf {lat,lng} eindampft.
 *
 * Exportiert, damit Einzelverortungen (freelancerService.verorteFreelancer)
 * denselben Weg gehen statt ihren eigenen `fetch` zu schreiben — sonst gaebe
 * es zwei Stellen, an denen die Proxy-URL steht.
 */
export const geocodeViaProxy = async (query: string): Promise<NominatimHit | null> => {
    const res = await fetch(`/api/osm-proxy?service=geocode&q=${encodeURIComponent(query)}`);
    if (!res.ok) throw new Error(`Adresssuche fehlgeschlagen (${res.status}).`);
    const data = await res.json();
    return Array.isArray(data) && data[0] ? (data[0] as NominatimHit) : null;
};

// Serialisierung des gesamten Laufs: zwei parallele Backfills wuerden das
// 1-req/s-Budget verdoppeln und Nominatim mit 429 antworten lassen (Muster aus
// osmMaps.ts, dort fuer Overpass).
let laeuft = false;

/**
 * Verortet alle Kunden ohne Koordinaten.
 *
 * Wirft nur bei Fehlern, die den ganzen Lauf betreffen (Kundenliste nicht
 * ladbar, Cache nicht lesbar). Ein Fehler an EINER Adresse landet in
 * `failures` und stoppt den Lauf nicht — sonst brichte eine einzige krumme
 * Adresse den Bestand ab.
 */
export const runGeocodeBackfill = async (
    options: GeocodeBackfillOptions = {},
): Promise<GeocodeBackfillResult> => {
    if (laeuft) throw new Error('Es läuft bereits eine Verortung. Bitte abwarten.');
    laeuft = true;
    try {
        const geocode = options.geocode ?? geocodeViaProxy;
        const throttleMs = options.throttleMs ?? 1100;
        const maxAttempts = options.maxAttempts ?? MAX_GEOCODE_ATTEMPTS;

        const customers =
            options.customers ?? ((await fetchCustomers({ includeArchived: !!options.includeArchived })) as GeocodeCustomerLike[]);
        const plan = planGeocodeBackfill(customers, options);
        const result: GeocodeBackfillResult = {
            ...plan,
            dryRun: !!options.dryRun,
            fromCache: 0,
            geocoded: 0,
            updated: 0,
            failures: [],
        };
        if (options.dryRun || plan.candidates.length === 0) return result;

        const cache = await lookupGeoCacheMany(plan.candidates.map((c) => c.addressHash));
        const total = plan.candidates.length;
        let done = 0;
        // Erst nach der ersten Netzanfrage pausieren — sonst kostet ein Lauf,
        // der komplett aus dem Cache bedient wird, unnoetig Zeit.
        let netzAufrufe = 0;

        for (const kandidat of plan.candidates) {
            options.onProgress?.({ done, total, current: kandidat.name });
            const bekannt = cache.get(kandidat.addressHash);

            try {
                // 1) Cache-Treffer mit Koordinaten: nichts zu erfragen.
                if (bekannt && hasCoordinates(bekannt)) {
                    result.fromCache++;
                    if (await updateCustomerGeo(kandidat.customerId, {
                        lat: bekannt.lat as number,
                        lng: bekannt.lng as number,
                        quality: (bekannt.quality ?? 'interpolated') as GeoQuality,
                    })) {
                        result.updated++;
                    }
                    continue;
                }

                // 2) Bekannter Fehlschlag, Budget aufgebraucht: Handarbeit.
                if (!shouldRetryCacheEntry(bekannt, maxAttempts)) {
                    result.failures.push({
                        customerId: kandidat.customerId,
                        name: kandidat.name,
                        address: kandidat.query,
                        reason: bekannt?.error
                            ? `${bekannt.error} (${bekannt.attempts} Versuche)`
                            : `Bereits ${bekannt?.attempts ?? maxAttempts}× erfolglos versucht`,
                    });
                    continue;
                }

                // 3) Nominatim fragen — gedrosselt.
                if (netzAufrufe > 0) await sleep(throttleMs);
                netzAufrufe++;
                const hit = await geocode(kandidat.query);
                const treffer = classifyGeocodeResult(hit, { hasStreet: kandidat.hasStreet });

                if (!treffer) {
                    const versuche = (bekannt?.attempts ?? 0) + 1;
                    await upsertGeoCache({
                        addressNorm: kandidat.addressNorm,
                        addressHash: kandidat.addressHash,
                        lat: null,
                        lng: null,
                        provider: 'nominatim',
                        quality: 'failed',
                        attempts: versuche,
                        error: 'Adresse nicht gefunden',
                        manual: false,
                        resolvedAt: null,
                    });
                    result.failures.push({
                        customerId: kandidat.customerId,
                        name: kandidat.name,
                        address: kandidat.query,
                        reason: 'Adresse nicht gefunden',
                    });
                    continue;
                }

                result.geocoded++;
                await upsertGeoCache({
                    addressNorm: kandidat.addressNorm,
                    addressHash: kandidat.addressHash,
                    lat: treffer.lat,
                    lng: treffer.lng,
                    provider: 'nominatim',
                    quality: treffer.quality,
                    attempts: (bekannt?.attempts ?? 0) + 1,
                    error: null,
                    manual: false,
                    resolvedAt: new Date().toISOString(),
                });
                if (await updateCustomerGeo(kandidat.customerId, {
                    lat: treffer.lat,
                    lng: treffer.lng,
                    quality: treffer.quality,
                })) {
                    result.updated++;
                }
            } catch (e: any) {
                result.failures.push({
                    customerId: kandidat.customerId,
                    name: kandidat.name,
                    address: kandidat.query,
                    reason: e?.message || 'Unbekannter Fehler',
                });
            } finally {
                done++;
            }
        }

        options.onProgress?.({ done, total, current: '' });
        return result;
    } finally {
        laeuft = false;
    }
};
